import React from 'react';
import { Typography } from '@material-ui/core/';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsHeatmap from 'highcharts/modules/heatmap';

/**
 * The `heatmap` result format — a labelled matrix.
 *
 *     heatmaps: [ { title, xLabel, yLabel, columns: [], rows: [], values: [[…]] } ]
 *
 * `values[r][c]` is the cell at row `r`, column `c`. A `null` cell is drawn as a gap rather than as zero,
 * which matters for the case this was built for: a centroid with no value for an attribute is missing data,
 * and colouring it as the bottom of the scale would assert something false.
 */

// The heatmap series type is a Highcharts module, not part of the core bundle.
if (typeof HighchartsHeatmap === 'function') HighchartsHeatmap(Highcharts);

export default function HeatmapChart({ data }) {
    const heatmaps = Array.isArray(data && data.heatmaps) ? data.heatmaps : [];
    if (!heatmaps.length) {
        return <Typography variant="body2" style={{ padding: 20, opacity: 0.7 }}>
            This result has no <code>heatmaps</code> block.
        </Typography>;
    }

    return <div>
        {heatmaps.map((map, i) => {
            const columns = (map.columns || []).map(String);
            const rows = (map.rows || []).map(String);
            const values = Array.isArray(map.values) ? map.values : [];

            const points = [];
            let min = null;
            let max = null;
            for (let r = 0; r < rows.length; ++r) {
                const row = Array.isArray(values[r]) ? values[r] : [];
                for (let c = 0; c < columns.length; ++c) {
                    const raw = row[c];
                    const v = (typeof raw === 'number' && isFinite(raw)) ? raw : null;
                    points.push([c, r, v]);
                    if (v !== null) {
                        min = (min === null || v < min) ? v : min;
                        max = (max === null || v > max) ? v : max;
                    }
                }
            }

            // Height grows with the row count: 40 attributes in a 400px box is an unreadable smear.
            const height = Math.min(1400, Math.max(320, 26 * rows.length + 140));

            const options = {
                chart: { type: 'heatmap', height, zoomType: 'xy' },
                title: { text: map.title || `Heatmap ${i + 1}` },
                xAxis: { categories: columns, title: { text: map.xLabel || '' } },
                yAxis: { categories: rows, title: { text: map.yLabel || '' }, reversed: true },
                colorAxis: {
                    min: min === null ? 0 : min,
                    max: max === null ? 1 : max,
                    minColor: '#ffffd9',
                    maxColor: '#225ea8'
                },
                credits: { enabled: false },
                legend: { align: 'right', layout: 'vertical', verticalAlign: 'middle', symbolHeight: height - 200 },
                tooltip: {
                    formatter: function formatter() {
                        const value = this.point.value;
                        return `<b>${rows[this.point.y]}</b><br/>${map.xLabel || 'column'}: `
                            + `<b>${columns[this.point.x]}</b><br/>value: `
                            + `<b>${value === null ? 'no data' : Highcharts.numberFormat(value, 4)}</b>`;
                    }
                },
                series: [{
                    name: map.title || 'value',
                    borderWidth: 1,
                    borderColor: 'rgba(0,0,0,0.08)',
                    data: points,
                    nullColor: '#eeeeee',
                    turboThreshold: 0        // a big matrix is legitimately a lot of points
                }]
            };

            return <div key={i} style={{ marginBottom: 20 }}>
                <HighchartsReact options={options} highcharts={Highcharts} />
                {map.note
                    ? <Typography variant="caption" style={{ opacity: 0.75 }}>{map.note}</Typography>
                    : null}
            </div>;
        })}
    </div>;
}
